import { useContext, useEffect, useState } from 'react';
import { Heart, MapPin, Star, BedDouble, Check, ArrowRight } from 'lucide-react';
import { UserAuthContext } from '../../context/UserAuthContext';
import { UserLoginSecContext } from '../../context/UserLoginSecContext';
import { handleWishlistClick } from '../../utils/wishlistHandler';
import { fetchWishlistData } from '../tripCard/getUserWishlist/getUserWishlist';
import { campStayQuery } from '../../utils/campStay';
import styles from './CampSearchResults.module.css';

const money = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

// Accommodation meal plans -> card text
const MEAL_LABELS = {
    breakfast: 'Breakfast included',
    half_board: 'Breakfast & dinner included',
    full_board: 'All meals included',
    all_meals: 'All meals included'
};

export function CampSearchToolbar({ total, sortBy, onSortChange }) {
    return (
        <div className={styles.toolbar}>
            <strong>{total ?? '—'} properties found</strong>
            <label>
                Sort by{' '}
                <select value={sortBy} onChange={(event) => onSortChange(event.target.value)}>
                    <option value='relevant'>Recommended</option>
                    <option value='low'>Price: Low to High</option>
                    <option value='high'>Price: High to Low</option>
                </select>
            </label>
            {/* <div className={styles.viewToggle}>
                <button className={styles.active}>▦ &nbsp;List</button>
                <button type='button' title='Map view coming soon'>
                    ♧ &nbsp;Map
                </button>
            </div> */}
        </div>
    );
}

export default function CampSearchResults({
    items = [],
    checkin,
    checkout,
    adults = 1,
    children = 0,
    pets = 0,
    detailsPath = (id) => `/camp/details/${id}` // v2 passes its own (id, title)
}) {
    const [wishlist, setWishlist] = useState([]);
    const { isAuthenticated } = useContext(UserAuthContext);
    const { setLogButton, setLoginSec } = useContext(UserLoginSecContext);
    useEffect(() => {
        fetchWishlistData('camp', setWishlist);
    }, [setLogButton]);
    // dates + guests go to the details page (prefilled in the booking tab)
    const openDetails = (id) =>
        window.open(
            `${detailsPath(id, items.find((item) => item._id === id)?.title)}?${campStayQuery({ checkin, checkout, adults, children, pets })}`,
            '_blank',
            'noopener,noreferrer'
        );
    const toggleWish = (id) =>
        isAuthenticated
            ? handleWishlistClick(id, wishlist, 'camp', setWishlist)
            : (setLogButton(true), setLoginSec('login'));

    return items.map((item) => {
        // Search sends `availability.unit` = the cheapest room/tent free for the
        // searched dates and guests (backend utils/search/campAvailability.js).
        // `units` is only a fallback for older data.
        const avail = item?.availability?.unit || null;
        const unit = item?.units?.[0] || {};
        const pricing = unit?.pricing?.[0] || {};
        const price = Number(avail?.price ?? pricing.actualPrice ?? pricing.basePrice ?? 0);
        const strike = Number(avail?.strikePrice ?? pricing.strikePrice ?? 0);
        const discount = Number(
            avail?.discountPercent ||
                pricing.discountPercent ||
                (strike > price && strike ? Math.round(((strike - price) / strike) * 100) : 0)
        );
        const meal = MEAL_LABELS[avail?.mealPlan] || '';
        const refundable = avail ? avail.refundable : false;
        const roomsNeeded = avail?.roomsNeeded > 1 ? `${avail.roomsNeeded} × ` : '';
        const images = item?.cardImages?.length
            ? item.cardImages
            : item?.media?.filter((m) => m.type === 'image').map((m) => m.url) || [];
        const location = [item?.address?.city, item?.address?.state].filter(Boolean).join(', ');
        const unitName = avail?.name || unit?.name || unit?.unitName || unit?.unitType || item?.propertyType || 'Stay';
        const guests = item?.stay?.guests || item?.maxGuests || unit?.maximumGuests || unit?.occupancy?.total || adults;
        const features = (item?.propertyHighlights || item?.highlights || []).slice(0, 3);
        const reviewCount = item?.rating?.count || item?.reviewCount || 0;
        const rating = Number(item?.rating?.average ?? item?.rating ?? 0);
        const favorite = Array.isArray(wishlist) && wishlist.includes(item._id);
        return (
            <article className={styles.card} key={item._id}>
                <button className={styles.favorite} onClick={() => toggleWish(item._id)} aria-label='Save stay'>
                    <Heart size={19} fill={favorite ? '#e11d48' : 'none'} color={favorite ? '#e11d48' : '#172554'} />
                </button>
                {discount > 0 && <span className={styles.discount}>{discount}% OFF</span>}
                <button
                    className={styles.imageButton}
                    onClick={() => openDetails(item._id)}
                    aria-label={`View ${item.title}`}
                >
                    {images.length ? (
                        <img src={images[0]} alt={item.title || 'Stay'} />
                    ) : (
                        <div className={styles.imageFallback}>Stay in {location || 'nature'}</div>
                    )}
                    <span className={styles.imageLocation}>
                        <MapPin size={13} fill='currentColor' /> {location || 'India'}
                    </span>
                    <span className={styles.imageType}>{item.propertyType || item.propertyCategory || 'Stay'}</span>
                </button>
                <div className={styles.info}>
                    {/* <div className={styles.eyebrow}>{item.propertyType || item.propertyCategory || 'STAY'} <span>·</span> {item.category || 'NATURE'}</div> */}
                    <button className={styles.title} onClick={() => openDetails(item._id)}>
                        {item.title || 'Stay'}
                    </button>
                    <div className={styles.rating}>
                        <Star size={15} fill='#f5a400' color='#f5a400' />
                        <b>{rating ? rating.toFixed(1) : 'New'}</b>
                        {reviewCount ? <span>({reviewCount} reviews)</span> : null}
                    </div>
                    {features.length > 0 && (
                        <div className={styles.features}>
                            {features.map((feature) => (
                                <span key={String(feature)}>
                                    {typeof feature === 'string' ? feature : feature.name}
                                </span>
                            ))}
                        </div>
                    )}
                    <div className={styles.booking}>
                        <div>
                            <BedDouble size={15} />
                            <span>
                                {roomsNeeded}
                                {unitName} · {guests} guests
                            </span>
                            {avail?.fewLeft && avail.left > 0 && (
                                <em className={styles.fewLeft}>Only {avail.left} left</em>
                            )}
                        </div>
                        <strong>
                            {strike > price && <del>{money(strike)}</del>}
                            {money(price)}
                            <small>per night + taxes</small>
                        </strong>
                    </div>
                    {(meal || refundable) && (
                        <div className={styles.perks}>
                            {meal && (
                                <span>
                                    <Check size={14} /> {meal}
                                </span>
                            )}
                            {refundable && (
                                <span>
                                    <Check size={14} /> Refundable
                                </span>
                            )}
                        </div>
                    )}
                    <button className={styles.details} onClick={() => openDetails(item._id)}>
                        View Details <ArrowRight size={16} />
                    </button>
                </div>
            </article>
        );
    });
}
